import { useEffect, useState } from "react";
import { SearchX, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, PhotoUpload, inp, lbl } from "./GearBits";

const STATUS_TONES = { open: "bg-red-100 text-red-700", in_progress: "bg-amber-100 text-amber-700", found: "bg-emerald-100 text-emerald-700", closed: "bg-zinc-100 text-zinc-600" };

export default function LostReports({ bags, items, trips, prefill, onPrefillUsed }) {
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/gear/lost-reports").then((r) => setReports(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (prefill) { setForm(prefill); onPrefillUsed?.(); } }, [prefill, onPrefillUsed]);

  const setStatus = async (r, status) => {
    await api.put(`/gear/lost-reports/${r.id}`, { status });
    toast.success(status === "found" ? "Marked found — great news!" : "Report updated");
    load();
  };

  return (
    <div data-testid="lost-reports">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Detailed reports make airline & hotel claims far more effective — photos and serial numbers help most.</p>
        <button onClick={() => setForm({})} data-testid="lost-new"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-red-600 text-white hover:bg-red-700 shrink-0">
          <Plus className="w-4 h-4" /> Report lost item
        </button>
      </div>
      {reports.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-8 text-center text-muted-foreground text-sm">
          <SearchX className="w-8 h-8 mx-auto mb-2 text-zinc-300" /> No lost items — may it stay that way!
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="border border-zinc-300 bg-white p-4 flex flex-wrap items-start gap-3" data-testid={`lost-report-${r.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-900 uppercase tracking-widest px-2 py-0.5 ${STATUS_TONES[r.status] || STATUS_TONES.open}`}>{r.status}</span>
                  <span className="font-bold text-sm">{r.title}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-x-3">
                  {r.last_seen_location && <span>Last seen: {r.last_seen_location}</span>}
                  {r.carrier && <span>Carrier: {r.carrier}</span>}
                  {r.report_number && <span>Report #: {r.report_number}</span>}
                  {r.serial_number && <span>Serial: {r.serial_number}</span>}
                </div>
                {r.description && <p className="text-sm mt-1.5">{r.description}</p>}
              </div>
              <div className="flex gap-2">
                {r.status !== "found" && <button onClick={() => setStatus(r, "found")} data-testid={`lost-found-${r.id}`} className="px-3 py-1.5 text-xs font-bold border border-emerald-500 text-emerald-700 hover:bg-emerald-50">Mark found</button>}
                {r.status !== "closed" && <button onClick={() => setStatus(r, "closed")} className="px-3 py-1.5 text-xs border border-zinc-300 hover:border-zinc-500">Close</button>}
                <button onClick={() => setForm(r)} className="px-3 py-1.5 text-xs border border-zinc-300 hover:border-primary hover:text-primary">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {form !== null && <ReportForm report={form} bags={bags} items={items} trips={trips} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function ReportForm({ report, bags, items, trips, onClose, onSaved }) {
  const [f, setF] = useState({ kind: "", ref_id: "", title: "", description: "", brand: "", model: "", color: "",
    serial_number: "", tag_number: "", last_seen_location: "", last_seen_at: "", carrier: "", property_name: "",
    report_number: "", contact: "", trip_id: "", photo_url: null, status: "open", ...report });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const pickRef = (v) => {
    const [kind, id] = v.split(":");
    const src = kind === "bag" ? bags.find((b) => b.id === id) : items.find((i) => i.id === id);
    setF({ ...f, kind, ref_id: id, title: f.title || src?.name || "", brand: f.brand || src?.brand || "",
      model: f.model || src?.model || "", color: f.color || src?.color || "",
      serial_number: f.serial_number || src?.serial_number || "", photo_url: f.photo_url || src?.photo_url || null });
  };

  const save = async () => {
    if (!f.title.trim()) return toast.error("What is lost? Add a title");
    setBusy(true);
    try {
      if (report.id) await api.put(`/gear/lost-reports/${report.id}`, f);
      else await api.post("/gear/lost-reports", f);
      toast.success(report.id ? "Report updated" : "Lost item report created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save report"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={report.id ? "Edit lost item report" : "Report a lost item"} onClose={onClose} wide>
      <div className="space-y-3" data-testid="lost-form">
        {!report.id && (
          <div><label className={lbl}>Link to a bag or item (prefills details)</label>
            <select value={f.kind && f.ref_id ? `${f.kind}:${f.ref_id}` : ""} onChange={(e) => e.target.value && pickRef(e.target.value)} data-testid="lost-ref" className={inp}>
              <option value="">— Not linked —</option>
              <optgroup label="Bags">{bags.map((b) => <option key={b.id} value={`bag:${b.id}`}>{b.name}</option>)}</optgroup>
              <optgroup label="Items">{items.map((i) => <option key={i.id} value={`item:${i.id}`}>{i.name}</option>)}</optgroup>
            </select></div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className={lbl}>What is lost? *</label>
            <input value={f.title} onChange={set("title")} data-testid="lost-title" className={inp} placeholder="Red checked suitcase" /></div>
          <div><label className={lbl}>Brand / model / color</label>
            <div className="flex gap-2">
              <input value={f.brand || ""} onChange={set("brand")} className={inp} placeholder="Brand" />
              <input value={f.color || ""} onChange={set("color")} className={inp} placeholder="Color" />
            </div></div>
          <div><label className={lbl}>Serial / tag number</label>
            <div className="flex gap-2">
              <input value={f.serial_number || ""} onChange={set("serial_number")} className={inp} placeholder="Serial" />
              <input value={f.tag_number || ""} onChange={set("tag_number")} className={inp} placeholder="Bag tag" />
            </div></div>
          <div><label className={lbl}>Last seen — where?</label><input value={f.last_seen_location || ""} onChange={set("last_seen_location")} data-testid="lost-lastseen" className={inp} placeholder="JFK Terminal 4, carousel 7" /></div>
          <div><label className={lbl}>Last seen — when?</label><input value={f.last_seen_at || ""} onChange={set("last_seen_at")} className={inp} placeholder="June 14, ~9 PM" /></div>
          <div><label className={lbl}>Airline / transport provider</label><input value={f.carrier || ""} onChange={set("carrier")} className={inp} placeholder="Delta DL123" /></div>
          <div><label className={lbl}>Hotel / property</label><input value={f.property_name || ""} onChange={set("property_name")} className={inp} /></div>
          <div><label className={lbl}>Official report number</label><input value={f.report_number || ""} onChange={set("report_number")} data-testid="lost-reportnum" className={inp} placeholder="Airline PIR / hotel case #" /></div>
          <div><label className={lbl}>Trip</label>
            <select value={f.trip_id || ""} onChange={set("trip_id")} className={inp}>
              <option value="">— Not linked —</option>
              {trips.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select></div>
        </div>
        <div><label className={lbl}>Description & contents</label>
          <textarea value={f.description || ""} onChange={set("description")} rows={2} className={inp} placeholder="Distinguishing marks, what was inside…" /></div>
        <PhotoUpload value={f.photo_url} onChange={(u) => setF({ ...f, photo_url: u })} testid="lost-photo-upload" />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="lost-save" className="px-5 py-2 text-sm font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {busy ? "Saving…" : "Save report"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
